import { useCallback, useMemo } from 'react';
import useBasket from '~/features/commerce-api/hooks/useBasket';
import { ascertainNumber } from '~/shared/utils/numbers';
import { AggregatedPromotions } from './type';

export const useAppliedSpecialPricing = () => {
    const { productItems } = useBasket();

    /**
     * This is an object that contains the aggregated and calculated state
     * of our special pricing campaigns on the basket.
     *
     * Example:
     * {
     *  'specialPrice20': {
     *      basketAmountThreshold: 300.00,
     *      amountInBasket: 223.99,
     *      // ...
     *  },
     *  'saveMore30': {
     *      basketNumberOfProductsThreshold: 1,
     *      quantityInBasket: 2,
     *      // ...
     *  }
     * }
     */
    const promotions = useMemo(() => {
        return productItems?.reduce<AggregatedPromotions>((collection, item) => {
            const {
                id: promotionId,
                effectivePrice: _1,
                /**
                 * amountInBasket and quantityInBasket is usually stale and is not reliable.
                 * We will generate our own.
                 */
                amountInBasket: _2,
                quantityInBasket: _3,
                retailPrice,
                ...necessaryProperties
            } = item.c_specialPrices || {};

            if (promotionId) {
                /**
                 * We will work with a matched promotion. If there's none, we will create a new entry.
                 */
                const promotion = collection?.[promotionId] || {
                    ...necessaryProperties,
                    id: promotionId,
                    amountInBasket: 0,
                    quantityInBasket: 0,
                    productIds: [] as string[],
                };

                return {
                    ...collection,
                    [promotionId]: {
                        ...promotion,
                        /**
                         * The campaign totals are accumulated as we go so they always reflect the
                         * basket as it is right now. A line qualifies on its retail price — the
                         * undiscounted, per-unit price the campaign is measured against — times
                         * the quantity on the line.
                         */
                        amountInBasket:
                            promotion.amountInBasket +
                            ascertainNumber(retailPrice, { parser: parseFloat }) *
                                ascertainNumber(item.quantity),
                        quantityInBasket:
                            promotion.quantityInBasket + ascertainNumber(item.quantity),
                        // We're making an assertion that item has productId always as this should be the case.
                        productIds: promotion.productIds.concat(item.productId!),
                    },
                };
            }

            return collection;
        }, undefined);
    }, [productItems]);

    const getProductPromotion = useCallback(
        (productId: string) => {
            if (!promotions) return undefined;

            const matchedPromotion = Object.values(promotions).find((promotion) =>
                promotion.productIds.includes(productId),
            );

            return matchedPromotion;
        },
        [promotions],
    );

    return {
        promotions,
        getProductPromotion,
    };
};
